import { getTopmostDialogElement, onDialogStackChange } from '@/core/dialogsystem/dialogQueue';

// Must equal vue3-toastify's CSS_NAMESPACE: when a `.Toastify` element already exists the library
// reuses it as its container instead of appending its own to document.body. That lets us own the
// element and move it into the top layer above open dialogs.
const TOAST_HOST_CLASS = 'Toastify';

let host: HTMLElement | null = null;

const supportsPopover = (): boolean => typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype;

const getHost = (): HTMLElement | null => {
  if (host) return host;
  if (typeof document === 'undefined') return null;

  host = document.querySelector<HTMLElement>(`.${TOAST_HOST_CLASS}`);
  if (!host) {
    host = document.createElement('div');
    host.className = TOAST_HOST_CLASS;
    document.body.appendChild(host);
  }
  return host;
};

const isPopoverOpen = (element: HTMLElement): boolean => element.matches(':popover-open');

const showAsPopover = (element: HTMLElement): void => {
  try {
    // Re-showing moves the popover back to the top of the top layer, above the newest dialog.
    if (isPopoverOpen(element)) element.hidePopover();
    element.showPopover();
  } catch {
    // showPopover throws if the element is disconnected or already open; the host stays put.
  }
};

const closePopover = (element: HTMLElement): void => {
  try {
    if (isPopoverOpen(element)) element.hidePopover();
  } catch {
    // ignore
  }
};

// Reparenting + re-showing the popover restart the toast's CSS animations (enter bounce + persistent
// icon zoom), replaying the entrance on every dialog change. Snap those to their end; the infinite
// loading spinner is left running. New toasts still animate (relocation only runs on dialog changes).
const settleReplayedAnimations = (element: HTMLElement): void => {
  if (typeof element.getAnimations !== 'function') return;
  // Force layout so the animations restarted by the display change already exist.
  void element.getBoundingClientRect();
  element.getAnimations({ subtree: true }).forEach((animation) => {
    if (animation.effect?.getComputedTiming().iterations === Infinity) return;
    try {
      animation.finish();
    } catch {
      // finish() throws for animations without a resolvable finite end; ignore.
    }
  });
};

// Keep the toast container in the topmost open dialog's subtree (a popover, so it paints above the
// dialog and is never clipped by its overflow/transform, yet stays interactive because it lives
// inside the non-inert dialog). With no dialog open it is a plain container on document.body.
const relocateToastHost = (): void => {
  const element = getHost();
  if (!element) return;

  const dialog = getTopmostDialogElement();

  if (dialog) {
    if (element.parentElement === dialog && isPopoverOpen(element)) return;
    if (element.getAttribute('popover') !== 'manual') element.setAttribute('popover', 'manual');
    if (element.parentElement !== dialog) dialog.appendChild(element);
    showAsPopover(element);
    settleReplayedAnimations(element);
    return;
  }

  if (element.parentElement === document.body && !element.hasAttribute('popover')) return;
  closePopover(element);
  element.removeAttribute('popover');
  if (element.parentElement !== document.body) document.body.appendChild(element);
  settleReplayedAnimations(element);
};

// Safe to call more than once: dialogStackListeners is a Set and relocateToastHost is a stable
// reference, so repeated calls don't add duplicate subscriptions.
export const initToastTopLayer = (): void => {
  if (typeof document === 'undefined' || !supportsPopover()) return;
  getHost();
  onDialogStackChange(relocateToastHost);
};
